import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize, Subscription } from 'rxjs';

import { CraditService } from '../../shared/services/cradit.service';
import {
  CreditGuarantorDto,
  DisbursementGuarantorDto,
  SaveDisbursementGuarantorPayload,
} from '../../shared/models/proposed-disbursement.model';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { AmountInput } from '../../../../core/shared/directives/amount-input.directive';
import { ToastService } from '../../../../core/shared/services/toast.service';
import { AppIcons } from '../../../../core/icons/fa-icons';
import { Table } from '../../../shared/table/table';
import { TableAction, TableColumn } from '../../../shared/models/table-models';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { DropdownOption, PKL_GRP } from '../../../../core/shared/models/picklist.model';

/** One saved share as shown in the listing. */
interface GuarantorShareRow extends DisbursementGuarantorDto {
  grntrNm: string;
}

type PopupMode = 'add' | 'edit' | 'view';

/**
 * "Guarantor Share" tab of the disbursement note. Shares are saved per
 * proposed disbursement (`save-prpsd-dsbmnt-grntr`) and listed from
 * `get-prpsd-dsbmnt-grntr`. Guarantors come from the client's guarantors
 * (`get-clnt-grntrs`) and a share may not exceed the selected guarantor's
 * guaranty ceiling (PKR).
 */
@Component({
  selector: 'app-guarantor-share',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe, Popup, AmountInput, Table],
  templateUrl: './guarantor-share.html',
  // Fixed layout so the table honours the column widths (name and share equal).
  styles: [':host ::ng-deep table.table { table-layout: fixed; width: 100%; }'],
})
export class GuarantorShare implements OnInit, OnDestroy {
  @Input() crdtPrpslSeq = 0;
  @Input() prpsdDsbmntSeq = 0;

  rows: GuarantorShareRow[] = [];
  rowsLoading = false;

  guarantorsLoading = false;
  guarantors: CreditGuarantorDto[] = [];

  /** Repayment terms — ref code group 13; the saved value is the `refCd`. */
  repaymentTerms: DropdownOption[] = [];

  showPopup = false;
  popupMode: PopupMode = 'add';
  saving = false;
  /** The saved row being viewed / edited; null while adding. */
  private activeRow: GuarantorShareRow | null = null;

  showDeleteConfirm = false;
  private rowToDelete: GuarantorShareRow | null = null;

  readonly columns: TableColumn[] = [
    { key: 'grntrNm', label: 'DISB_NOTE.GUARANTOR_NAME', width: '30%' },
    { key: 'grntrShare', label: 'DISB_NOTE.GUARANTOR_SHARE', type: 'currency', align: 'left', width: '25%' },
    { key: 'rpymntTrmNm', label: 'DISB_NOTE.REPAYMENT_TERM', width: '25%' },
  ];

  /** View mode: rows can only be viewed — no add, edit or delete. */
  @Input() readOnly = false;

  get tableActions(): TableAction[] {
    return this.readOnly ? this.actions.slice(0, 1) : this.actions;
  }

  readonly actions: TableAction[] = [
    {
      icon: AppIcons.view,
      tooltip: 'COMMON.VIEW',
      class: 'text-blue-600',
      onClick: (row: GuarantorShareRow) => this.openPopup('view', row),
    },
    {
      icon: AppIcons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'text-indigo-600',
      onClick: (row: GuarantorShareRow) => this.openPopup('edit', row),
    },
    {
      icon: AppIcons.delete,
      tooltip: 'COMMON.DELETE',
      class: 'text-red-600',
      onClick: (row: GuarantorShareRow) => this.askToDelete(row),
    },
  ];

  readonly form = new FormGroup({
    grntrSeq: new FormControl<number | null>(null, { validators: [Validators.required] }),
    share: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(1), (control) => this.ceilingValidator(control)],
    }),
    rpymntTrm: new FormControl<number | null>(null, { validators: [Validators.required] }),
  }, { validators: (group) => this.duplicateShareValidator(group) });

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly creditService: CraditService,
    private readonly toast: ToastService,
    private readonly pickListService: PickListService,
  ) {
    // The share limit depends on the selected guarantor, so re-check it on change.
    this.subscriptions.add(
      this.form.controls.grntrSeq.valueChanges.subscribe(() =>
        this.form.controls.share.updateValueAndValidity({ emitEvent: false }),
      ),
    );
  }

  ngOnInit(): void {
    // Guarantor names are needed to label the saved rows.
    this.loadGuarantors();
    this.loadRows();
    this.loadRepaymentTerms();
  }


  get popupTitle(): string {
    return this.popupMode === 'edit'
      ? 'DISB_NOTE.EDIT_GUARANTOR_SHARE'
      : this.popupMode === 'view'
        ? 'DISB_NOTE.VIEW_GUARANTOR_SHARE'
        : 'DISB_NOTE.ADD_GUARANTOR_SHARE';
  }

  /** Every guarantor of the proposal — one guarantor may hold several shares. */
  get availableGuarantors(): CreditGuarantorDto[] {
    return this.guarantors;
  }

  get selectedGuarantor(): CreditGuarantorDto | undefined {
    const seq = this.form?.controls.grntrSeq.value;

    return this.guarantors.find((grntr) => Number(grntr.grntrSeq) === Number(seq));
  }

  openPopup(mode: PopupMode, row: GuarantorShareRow | null = null): void {
    this.popupMode = mode;
    this.activeRow = row;

    this.form.reset({
      grntrSeq: row ? Number(row.grntrSeq) : null,
      share: row ? Number(row.grntrShare) : null,
      rpymntTrm: row?.rpymntTrm != null ? Number(row.rpymntTrm) : null,
    });

    if (mode === 'view') {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }

    this.showPopup = true;
    this.loadGuarantors();
  }

  save(): void {
    if (this.popupMode === 'view') {
      this.showPopup = false;
      return;
    }

    this.form.markAllAsTouched();

    const { grntrSeq, share, rpymntTrm } = this.form.getRawValue();

    if (
      this.form.invalid ||
      grntrSeq == null ||
      share == null ||
      rpymntTrm == null ||
      this.saving
    ) {
      return;
    }

    this.submit(
      {
        prpsdDsbmntGrntrSeq: this.activeRow?.prpsdDsbmntGrntrSeq ?? null,
        prpsdDsbmntSeq: this.prpsdDsbmntSeq,
        grntrSeq: Number(grntrSeq),
        grntrShare: share,
        rpymntTrm: Number(rpymntTrm),
        delFlg: 0,
      },
      () => {
        this.showPopup = false;
        this.toast.success('DISB_NOTE.GUARANTOR_SHARE_SAVED');
      },
    );
  }

  askToDelete(row: GuarantorShareRow): void {
    this.rowToDelete = row;
    this.showDeleteConfirm = true;
  }

  confirmDelete(): void {
    const row = this.rowToDelete;

    this.rowToDelete = null;

    if (!row) {
      return;
    }

    this.submit(
      {
        prpsdDsbmntGrntrSeq: row.prpsdDsbmntGrntrSeq,
        prpsdDsbmntSeq: this.prpsdDsbmntSeq,
        grntrSeq: Number(row.grntrSeq),
        grntrShare: Number(row.grntrShare) || 0,
        rpymntTrm: Number(row.rpymntTrm ?? 0),
        delFlg: 1,
      },
      () => this.toast.success('DISB_NOTE.GUARANTOR_SHARE_DELETED'),
    );
  }

  isInvalid(control: AbstractControl): boolean {
    return control.invalid && (control.touched || control.dirty);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Saves, then reloads the listing from the server. */
  private submit(payload: SaveDisbursementGuarantorPayload, onSuccess: () => void): void {
    this.saving = true;

    this.subscriptions.add(
      this.creditService
        .saveDisbursementGuarantor(payload)
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: (response: any) => {
            // A 200 can still carry an error body: the error interceptor has
            // already toasted it and flagged the body, so no success here.
            if (response?.__hasError) {
              return;
            }

            if (response?.error) {
              this.toast.error(response.error);
              return;
            }

            onSuccess();
            this.loadRows();
          },
          error: (error: unknown) => {
            console.error('Unable to save guarantor share:', error);
            this.toast.error('DISB_NOTE.ERR_SAVE_GUARANTOR_SHARE');
          },
        }),
    );
  }

  private loadRows(): void {
    if (this.prpsdDsbmntSeq <= 0) {
      this.rows = [];
      return;
    }

    this.rowsLoading = true;

    this.subscriptions.add(
      this.creditService
        .getDisbursementGuarantors(this.prpsdDsbmntSeq)
        .pipe(finalize(() => (this.rowsLoading = false)))
        .subscribe({
          next: (response) => {
            const rows = Array.isArray(response) ? response : [];

            this.rows = rows
              .filter((row) => Number(row.delFlg) !== 1)
              .map((row) => ({ ...row, grntrNm: this.guarantorName(row) }));
          },
          error: (error: unknown) => {
            console.error('Unable to load guarantor shares:', error);
            this.rows = [];
            this.toast.error('DISB_NOTE.ERR_LOAD_GUARANTOR_SHARES');
          },
        }),
    );
  }

  private loadGuarantors(): void {
    if (this.prpsdDsbmntSeq <= 0) {
      this.guarantors = [];
      return;
    }

    this.guarantorsLoading = true;

    this.subscriptions.add(
      this.creditService
        .getClientGuarantors(this.prpsdDsbmntSeq)
        .pipe(finalize(() => (this.guarantorsLoading = false)))
        .subscribe({
          next: (response) => {
            this.guarantors = this.extractGuarantors(response);
            // Rows may have loaded first — label them now that names are known.
            this.rows = this.rows.map((row) => ({ ...row, grntrNm: this.guarantorName(row) }));
            this.form.controls.share.updateValueAndValidity({ emitEvent: false });
          },
          error: (error: unknown) => {
            console.error('Unable to load guarantors:', error);
            this.guarantors = [];
            this.toast.error('DISB_NOTE.ERR_LOAD_GUARANTORS');
          },
        }),
    );
  }

  /**
   * get-clnt-grntrs rows: a plain list, or wrapped in `grntrs` and/or `data`.
   * Deleted rows are dropped.
   */
  private extractGuarantors(response: unknown): CreditGuarantorDto[] {
    const source = response as { data?: unknown; grntrs?: unknown } | null;
    const data = source?.data as { grntrs?: unknown } | undefined;

    const list = [source, source?.grntrs, data, data?.grntrs].find(Array.isArray) as
      | Record<string, unknown>[]
      | undefined;

    return (list ?? [])
      .filter((row) => !!row && typeof row === 'object' && Number(row['delFlg'] ?? 0) !== 1)
      .map((row) => row as unknown as CreditGuarantorDto);
  }

  private loadRepaymentTerms(): void {
    this.subscriptions.add(
      this.pickListService.loadCodesAsync([PKL_GRP.FACILITY_TYPE]).subscribe({
        next: () => {
          this.repaymentTerms = this.pickListService
            .getOptionsSync(PKL_GRP.FACILITY_TYPE)
            // The backend stores the ref code (17, 18, 30), not refCdSeq.
            .map((option) => ({ ...option, value: Number(option.original?.refCd ?? option.value) }));
        },
        error: (error: unknown) => {
          console.error('Unable to load repayment terms:', error);
          this.repaymentTerms = [];
        },
      }),
    );
  }

  /** Name from the share row when the API sends it, else from the proposal's guarantors. */
  private guarantorName(row: DisbursementGuarantorDto): string {
    return (
      row.grntrNm ||
      this.guarantors.find((grntr) => Number(grntr.grntrSeq) === Number(row.grntrSeq))?.grntrNm ||
      `#${row.grntrSeq}`
    );
  }

  /**
   * A guarantor can hold only one share per repayment term type (e.g. ifc +
   * Conventional once, ifc + Islamic once). The pair picked in the popup is
   * compared with the saved shares in the table; the row being edited is
   * skipped so it can be saved unchanged.
   */
  private duplicateShareValidator(group: AbstractControl): ValidationErrors | null {
    const grntrSeq = group.get('grntrSeq')?.value;
    const rpymntTrm = group.get('rpymntTrm')?.value;

    if (grntrSeq == null || rpymntTrm == null) {
      return null;
    }

    const duplicate = this.rows.some(
      (row) =>
        row.prpsdDsbmntGrntrSeq !== this.activeRow?.prpsdDsbmntGrntrSeq &&
        Number(row.grntrSeq) === Number(grntrSeq) &&
        Number(row.rpymntTrm) === Number(rpymntTrm),
    );

    return duplicate ? { duplicateShare: true } : null;
  }

  /** Share may not exceed the selected guarantor's guaranty ceiling (PKR). */
  private ceilingValidator(control: AbstractControl): ValidationErrors | null {
    // Runs once while `form` is still being built, before any value exists.
    if (control.value == null || !this.form) {
      return null;
    }

    const guarantor = this.selectedGuarantor;
    const ceiling = Number(guarantor?.grntyCeilngPkr);

    if (!guarantor || Number.isNaN(ceiling)) {
      return null;
    }

    return Number(control.value) > ceiling ? { ceilingExceeded: { ceiling } } : null;
  }
}
